import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { withApplicationContext } from '../../utils/ApplicationContext';
import _ from '../../utils/LodashImports';
import { noop } from '../../utils/noop';
import { mapATGPricesToCatalystPrices } from '../../utils/PriceHelpers';
import { withProduct } from '../../utils/ProductContext';
import MatchMedia from '../ui/MatchMedia/MatchMedia';
import { ComparativeTableStyles } from './ComparativeTable.style';
import ComparativeTablePod from './ComparativeTablePod/ComparativeTablePod';
import ComparativeTableTab from './ComparativeTableTab/ComparativeTableTab';

const getPayloadTitle = (rawTabSelectedObject) => {
  return `${rawTabSelectedObject.index}|${rawTabSelectedObject.text.split(' ').join('_')}`;
};

const ComparativeTable = ({
  products,
  appCtx,
  heading,
  carouselPosition,
  parentVariantID,
  handleViewedProducts,
  handleAddedToCartProducts,
  handleClickedProduct,
  addToCart,
  attributes,
  product,
  slotId,
}) => {
  const similarUniqueViewedProducts = new Set();
  const { regionCode } = appCtx;
  const toggles = _.get(appCtx, 'siteConfig.toggles', {});
  const comparativeTableTitle = _.get(
    appCtx,
    'siteConfig.textDictionary.COMPARATIVE_TABLE_TITLE',
    'Encuentra la opción correcta'
  );
  const driverBadgeLabel = _.get(appCtx, 'siteConfig.textDictionary.DRIVER_BADGE_LABEL', 'Estás viendo');
  const driverBadgeConfig = _.get(appCtx, 'siteConfig.configurations.driverBadgeConfig', {});
  const showDriverInComparativeTable = _.get(appCtx, 'siteConfig.toggles.showDriverInComparativeTable', false);
  const carouselRecoRef = React.useRef(null);
  const [headingRawTabSelected, setHeadingRawTabSelected] = useState({
    index: 0,
    text: 'Productos similares',
  });

  const getProductAttributes = (productData) => {
    const specifications = _.get(productData, 'attributes.specifications', []);

    return specifications.reduce((result, spec) => {
      if (spec && spec.name) {
        return { ...result, [spec.name]: spec.value };
      }
      return result;
    }, {});
  };
  const getImageMedia = (medias) => medias.find((media) => media.mediaType?.toLowerCase() === 'image');

  const getProductInfo = (productData) => {
    const { id, name, brandName, medias = [] } = productData;
    let mediaImageUrls = getImageMedia(medias);
    if (!mediaImageUrls?.length) {
      const { variants, currentVariant: currentVariantId } = productData;
      const currentVariant = variants?.find((variant) => variant?.id === currentVariantId);
      const { medias: mediasVarient = [] } = currentVariant || {};
      mediaImageUrls = getImageMedia(mediasVarient);
    }
    const mediaImageUrl = mediaImageUrls?.url;
    const prices = _.get(productData, 'variants[0].prices', []);
    const offeringId = _.get(productData, 'variants[0].offerings[0].offeringId', '');
    const badges = _.get(productData, 'variants[0].badges', []);
    const meatStickers = _.get(productData, 'variants[0].meatStickers', []);
    const discountBadge = _.get(productData, 'variants[0].discountBadge', null);
    const productVariantId = _.get(productData, 'variants[0].id', '');

    return {
      id: productVariantId,
      productId: id,
      displayName: name,
      brand: brandName,
      offeringId,
      prices: prices.map(({ unitPrice: _unitPrice, ...rest }) => rest),
      attributes: getProductAttributes(productData),
      badges: [
        ...badges,
        {
          type: driverBadgeConfig?.type || 'DRIVER_BADGE',
          label: driverBadgeLabel,
          styles: {
            backgroundColor: driverBadgeConfig?.backgroundColor || '#717171',
            textColor: driverBadgeConfig?.textColor || '#FFFFFF',
          },
        },
      ],
      mediaUrls: mediaImageUrl ? [mediaImageUrl] : [],
      meatStickers,
      discountBadge,
    };
  };

  const currentProductInfo = getProductInfo(product);
  const comparativeTableData = showDriverInComparativeTable
    ? [
        {
          ...currentProductInfo,
        },
        ...products.slice(0, 5).map((p) => ({
          ...p,
          prices: mapATGPricesToCatalystPrices(p.prices, regionCode, toggles),
        })),
      ]
    : products.map((p) => ({
        ...p,
        prices: mapATGPricesToCatalystPrices(p.prices, regionCode, toggles),
      }));

  const aggregateViewedProducts = ({ sku, selectedTab }) => {
    if (selectedTab === 'similar') {
      similarUniqueViewedProducts.add(sku);
      const skus = Array.from(similarUniqueViewedProducts).join(',');
      handleViewedProducts(heading, skus);
    }
  };
  const podClicked = (id) => {
    handleClickedProduct(headingRawTabSelected.index, id);
  };
  const addedToCart = (id) => {
    handleAddedToCartProducts(headingRawTabSelected.index, id);
  };

  const showRecommendationsWrapper = comparativeTableData.length > 0;

  const similarProducts = comparativeTableData.map((item, index) => (
    <ComparativeTablePod
      key={item.productId}
      product={item}
      appCtx={appCtx}
      aggregateViewedProducts={aggregateViewedProducts}
      podClicked={podClicked}
      addedToCart={addedToCart}
      attributes={attributes}
      details={{
        numberProducts: comparativeTableData.length,
        productPosition: index + 1,
        carouselPosition,
        carouselName: heading,
        parentVariantID,
        selectedTab: 'similar',
        slotId,
      }}
      addToCart={addToCart}
      totalProducts={comparativeTableData.length}
      currentVariant={product.currentVariant}
      slot={carouselPosition}
      showDriverInComparativeTable={showDriverInComparativeTable}
    />
  ));

  // The rest of them will be the N attributes received from the API. ...
  // attributes rows....
  function createAttributeRowsData(fnAttributes, fnProducts) {
    const tableData = {
      attributes: [],
    };

    fnAttributes.forEach((attr) => {
      const attrColumn = fnProducts.map((p) => {
        if (p.attributes && Object.prototype.hasOwnProperty.call(p.attributes, attr)) {
          return p.attributes[attr];
        }
        return '';
      });
      const attrObject = {};
      attrObject.name = attr;
      attrObject.data = attrColumn;
      tableData.attributes.push(attrObject);
    });

    return tableData;
  }

  const attrRowsData = createAttributeRowsData(attributes, comparativeTableData);

  return (
    showRecommendationsWrapper && (
      <div data-widget-name={heading} ref={carouselRecoRef} className="comparative-table-wrapper">
        <div className="comparative-table-heading">{comparativeTableTitle}</div>
        <MatchMedia
          content={{
            mobile: () => (
              <ComparativeTableTab
                deviceType="mobile"
                setHeadingRawTabSelected={setHeadingRawTabSelected}
                appCtx={appCtx}
                mProducts={comparativeTableData}
                podClicked={podClicked}
                addedToCart={addedToCart}
                carouselPosition={carouselPosition}
                heading={heading}
                parentVariantID={parentVariantID}
                addToCart={addToCart}
                similarProducts={similarProducts}
                sameSellerProducts={[]}
                attrRowsData={attrRowsData}
              />
            ),
            tablet: () => (
              <ComparativeTableTab
                deviceType="mobile"
                setHeadingRawTabSelected={setHeadingRawTabSelected}
                appCtx={appCtx}
                mProducts={comparativeTableData}
                podClicked={podClicked}
                addedToCart={addedToCart}
                carouselPosition={carouselPosition}
                heading={heading}
                parentVariantID={parentVariantID}
                addToCart={addToCart}
                similarProducts={similarProducts}
                sameSellerProducts={[]}
                attrRowsData={attrRowsData}
              />
            ),
            desktop: () => (
              <ComparativeTableTab
                deviceType="desktop"
                setHeadingRawTabSelected={setHeadingRawTabSelected}
                appCtx={appCtx}
                mProducts={comparativeTableData}
                podClicked={podClicked}
                addedToCart={addedToCart}
                carouselPosition={carouselPosition}
                heading={heading}
                parentVariantID={parentVariantID}
                addToCart={addToCart}
                similarProducts={similarProducts}
                sameSellerProducts={[]}
                attrRowsData={attrRowsData}
              />
            ),
          }}
        />
        <style jsx>{ComparativeTableStyles}</style>
      </div>
    )
  );
};
ComparativeTable.defaultProps = {
  handleViewedProducts: noop,
  handleAddedToCartProducts: noop,
  handleClickedProduct: noop,
  addToCart: noop,
  attributes: [],
  slotId: '',
};

ComparativeTable.propTypes = {
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  appCtx: PropTypes.object.isRequired,
  heading: PropTypes.object.isRequired,
  product: PropTypes.object.isRequired,
  carouselPosition: PropTypes.object.isRequired,
  parentVariantID: PropTypes.string.isRequired,
  handleViewedProducts: PropTypes.func,
  handleAddedToCartProducts: PropTypes.func,
  handleClickedProduct: PropTypes.func,
  addToCart: PropTypes.func,
  attributes: PropTypes.array,
  slotId: PropTypes.string,
};

export default withApplicationContext(withProduct(ComparativeTable));
export { ComparativeTable, getPayloadTitle };
